/**
 * Input component with QuantumCue design system styling.
 */

import React, { forwardRef, useState } from "react";
import { Eye, EyeOff } from "lucide-react";

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
  icon?: React.ReactNode;
  required?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, helperText, icon, className = "", id, required, ...props }, ref) => {
    const inputId = id || props.name || Math.random().toString(36).substr(2, 9);
    const [showPassword, setShowPassword] = useState(false);
    const isPassword = props.type === "password";

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={inputId}
            className="block text-sm font-semibold text-grey-700 dark:text-text-primary mb-1"
          >
            {label}
            {required && <span className="text-red-500">*</span>}
          </label>
        )}
        <div className="relative">
          {icon && (
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              {icon}
            </div>
          )}
          <input
            ref={ref}
            id={inputId}
            className={`
              w-full ${icon ? "pl-10" : "pl-3"} ${isPassword ? "pr-10" : "pr-3"} py-2
              bg-white dark:bg-surface text-sm
              border border-grey-300 dark:border-grey-700 rounded-lg
              text-grey-900 dark:text-text-primary
              placeholder:text-grey-400 dark:placeholder:text-text-tertiary
              transition-colors
              focus:outline-none focus:ring-2 focus:ring-navy-900/20 dark:focus:ring-navy-700/20 focus:border-[#3850A0]/60 dark:focus:border-brand-300/60
              disabled:opacity-50 disabled:cursor-not-allowed
              ${error ? "border-red-500 dark:border-red-500" : "border-grey-300 dark:border-grey-700 hover:border-grey-200 dark:hover:border-grey-600"}
              ${className}
            `}
            aria-invalid={error ? "true" : undefined}
            aria-describedby={
              error
                ? `${inputId}-error`
                : helperText
                  ? `${inputId}-helper`
                  : undefined
            }
            {...props}
            type={
              isPassword ? (showPassword ? "text" : "password") : props.type
            }
          />
          {isPassword && (
            <button
              type="button"
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-grey-400 hover:text-grey-600 dark:hover:text-text-secondary"
              onClick={() => setShowPassword(!showPassword)}
              tabIndex={-1}
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          )}
        </div>
        {error && (
          <p
            id={`${inputId}-error`}
            className="mt-1.5 text-sm text-error"
            role="alert"
          >
            {error}
          </p>
        )}
        {helperText && !error && (
          <p
            id={`${inputId}-helper`}
            className="mt-1.5 text-sm text-grey-500 dark:text-text-secondary"
          >
            {helperText}
          </p>
        )}
      </div>
    );
  },
);

Input.displayName = "Input";

export default Input;
